import {
  AlarmClock,
  ArrowLeft,
  Bot,
  Check,
  ChevronRight,
  Columns3,
  FileSpreadsheet,
  Inbox,
  Loader2,
  Mail,
  PackageCheck,
  Search,
  Truck,
  Warehouse,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import { NuovoCaricoDialog, type InizioNuovoCarico } from "@/components/m3/NuovoCaricoDialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useMailOffertaProposta,
  useMerceProntaClienti,
  useMerceProntaCliente,
  useScaricaOfferta,
} from "@/hooks/useM3";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import type {
  CategoriaMerce,
  MerceProntaCliente,
  OffertaMerceBreve,
  RigaMercePronta,
} from "@/types/api";

/**
 * «Commesse pronte» (03-10-2026).
 *
 * Con alcuni clienti Marina manda un Excel con la merce pronta delle commesse
 * aperte, e il cliente sceglie cosa ritirare. Oggi lo fa a mano da Business
 * Cube: scarica, toglie colonne, toglie righe. Qui lo stesso lavoro parte già
 * fatto — tutto il pronto spuntato, lei toglie — e il file esce come il suo.
 * Dalla stessa scelta si prepara la mail (bozza .eml per Outlook) o si crea il
 * carico. Ogni offerta resta registrata: è il dato che Enzo non aveva.
 *
 * 08-10-2026: la merce su una NDP **aperta** (senza DDT) è pronta ma già
 * destinata a un camion — Cube scala il residuo solo al DDT. Ogni riga dice
 * quanto è prenotato e quanto resta libero; i clienti si ordinano per pallet
 * liberi; nell'offerta va solo la parte libera.
 *
 * 08-10-2026, seconda parte: la **tolleranza sul ritiro**. La merce pronta e
 * libera che il cliente lascia qui oltre i giorni concordati (il suo accordo, o
 * la linea guida della proprietà nelle Impostazioni) è «oltre tolleranza»: è
 * la merce da sollecitare. Il conto è pallet per pallet e parte dalla data più
 * tarda fra consegna ed entrata a magazzino — vedi `merce_pronta.py`.
 */

const fmt = (n: number | null | undefined, cifre = 2) =>
  n == null ? "—" : new Intl.NumberFormat("it-IT", { maximumFractionDigits: cifre }).format(n);
const data = (iso: string | null | undefined) =>
  iso ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString("it-IT") : "—";
const dataOra = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const FILTRI: { id: "tutto" | CategoriaMerce | "oltre"; label: string }[] = [
  { id: "tutto", label: "Tutto" },
  { id: "magazzino", label: "Magazzino" },
  { id: "programmato", label: "Programmato" },
  { id: "non_disponibile", label: "Non disponibile" },
  { id: "oltre", label: "Oltre tolleranza" },
];

const oltre = (r: RigaMercePronta) => (r.pallet_oltre ?? 0) > 0;

export function MercePronteView() {
  const [cliente, setCliente] = useState<number | null>(null);
  return cliente == null ? (
    <ElencoClienti onApri={setCliente} />
  ) : (
    <DettaglioCliente codice={cliente} onIndietro={() => setCliente(null)} />
  );
}

// ── L'elenco dei clienti ──────────────────────────────────────────────────

/** I riquadri in testa: sono insieme il numero e il filtro, come sul tabellone. */
type FiltroClienti = "pronti" | "prenotati" | "oltre";

function ElencoClienti({ onApri }: { onApri: (codice: number) => void }) {
  const { data: dati, isLoading } = useMerceProntaClienti();
  const [q, setQ] = useState("");
  // Acceso di default «Pronti»: è la vista di prima («solo chi ha merce a
  // magazzino»). Ricliccare il riquadro acceso lo spegne: tutti i clienti.
  const [filtro, setFiltro] = useState<FiltroClienti | null>("pronti");
  const clienti = useMemo(() => {
    const t = q.trim().toLowerCase();
    const out = (dati?.clienti ?? []).filter(
      (c) =>
        (filtro !== "pronti" || c.righe_magazzino > 0) &&
        (filtro !== "prenotati" || c.pallet_prenotati > 0) &&
        (filtro !== "oltre" || c.pallet_oltre > 0) &&
        (!t || c.cliente_ragione_sociale.toLowerCase().includes(t))
    );
    // Fra chi è oltre tolleranza, in cima chi ne ha di più: è la lista dei solleciti.
    return filtro === "oltre" ? [...out].sort((a, b) => b.pallet_oltre - a.pallet_oltre) : out;
  }, [dati, q, filtro]);
  const tot = dati?.totali;
  const scegli = (f: FiltroClienti) => setFiltro((x) => (x === f ? null : f));

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Commesse pronte</h2>
        <p className="text-[13px] text-muted-foreground">
          La merce pronta a magazzino, cliente per cliente: si sceglie cosa offrire e si scarica il
          file, si prepara la mail o si crea il carico.
          {dati?.situazione_al && <> Situazione al {dataOra(dati.situazione_al)}.</>}
        </p>
      </div>

      <div
        className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3"
        data-testid="kpi-pronte"
      >
        <RiquadroKpi
          id="pronti"
          icona={Warehouse}
          label="Pallet pronti a magazzino"
          valore={tot?.pallet_magazzino}
          attivo={filtro === "pronti"}
          onClick={() => scegli("pronti")}
        >
          {tot && (
            <>
              <BarraPrenotati prenotati={tot.pallet_prenotati} totale={tot.pallet_magazzino} />
              <span>
                di cui <b className="text-sky-800">{fmt(tot.pallet_prenotati, 1)} prenotati</b> ·{" "}
                <b className="text-foreground">{fmt(tot.pallet_liberi, 1)} liberi</b> ·{" "}
                {tot.clienti_con_merce} clienti
              </span>
            </>
          )}
        </RiquadroKpi>
        <RiquadroKpi
          id="prenotati"
          icona={Truck}
          label="Prenotati (NDP aperta)"
          valore={tot?.pallet_prenotati}
          attivo={filtro === "prenotati"}
          onClick={() => scegli("prenotati")}
        >
          {tot && (
            <span
              title={
                tot.ndp_senza_merce_pronta.length
                  ? `NDP aperte che non toccano merce pronta: ${tot.ndp_senza_merce_pronta.join(", ")}`
                  : undefined
              }
            >
              {tot.ndp_aperte} NDP aperte senza DDT · {tot.clienti_prenotati} clienti
              {tot.ndp_senza_merce_pronta.length > 0 && (
                <> · {tot.ndp_senza_merce_pronta.length} senza merce pronta</>
              )}
            </span>
          )}
        </RiquadroKpi>
        <RiquadroKpi
          id="oltre"
          icona={AlarmClock}
          label="Oltre la tolleranza sul ritiro"
          valore={tot?.pallet_oltre}
          urgente
          attivo={filtro === "oltre"}
          onClick={() => scegli("oltre")}
        >
          {tot && (
            <span title="Merce pronta e libera che il cliente non ha ritirato entro i giorni concordati, contati dalla consegna o dall'entrata a magazzino se è venuta dopo">
              {tot.clienti_oltre} clienti · linea guida {tot.tolleranza_globale} giorni
              {tot.accordi > 0 && (
                <>
                  {" "}
                  · {tot.accordi} {tot.accordi === 1 ? "accordo" : "accordi"}
                </>
              )}
            </span>
          )}
        </RiquadroKpi>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[15rem] flex-1 sm:max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca cliente…"
            className="h-[34px] w-full rounded-md border bg-card pl-8 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <span className="text-[12px] text-muted-foreground">
          {filtro === "pronti"
            ? "Chi ha merce a magazzino"
            : filtro === "prenotati"
              ? "Chi ha merce su una NDP aperta"
              : filtro === "oltre"
                ? "Chi ha merce ferma oltre la tolleranza"
                : "Tutti i clienti con commesse aperte"}
          {filtro === "oltre"
            ? " · in cima chi ne ha di più"
            : " · in cima chi ha più pallet liberi"}
        </span>
        <span className="ml-auto text-[12px] text-muted-foreground">
          {clienti.length} {clienti.length === 1 ? "cliente" : "clienti"}
        </span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
        {isLoading ? (
          <div className="space-y-2 p-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-11 w-full" />
            ))}
          </div>
        ) : clienti.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
            <Inbox className="h-7 w-7" />
            <div className="text-sm font-medium">Nessun cliente in questa vista.</div>
          </div>
        ) : (
          <table className="w-full border-collapse" data-testid="elenco-clienti-pronte">
            <thead>
              <tr className="border-b border-border bg-brand-50/40 text-left text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2.5">Cliente</th>
                <th className="px-3 py-2.5 text-right">Commesse pronte</th>
                <th className="px-3 py-2.5 text-right">Pallet a magazzino</th>
                <th className="px-3 py-2.5">Prenotati (NDP)</th>
                <th className="px-3 py-2.5 text-right">Liberi</th>
                <th className="px-3 py-2.5">Oltre tolleranza</th>
                <th className="px-3 py-2.5 text-right">Righe programmate</th>
                <th className="px-3 py-2.5">Ultima offerta</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {clienti.map((c) => (
                <tr
                  key={c.cliente_codice}
                  data-cliente={c.cliente_codice}
                  onClick={() => onApri(c.cliente_codice)}
                  className="cursor-pointer border-b border-border text-[13px] last:border-0 hover:bg-muted/40"
                >
                  <td className="px-3 py-2.5 font-semibold">{c.cliente_ragione_sociale}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {c.commesse_pronte}
                    <span className="text-muted-foreground"> / {c.commesse}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{fmt(c.pallet_magazzino)}</td>
                  <td className="px-3 py-2.5" data-testid="prenotati-cliente">
                    {c.pallet_prenotati > 0 ? (
                      <span className="inline-flex flex-wrap items-center gap-1.5">
                        <span className="tabular-nums text-sky-900">{fmt(c.pallet_prenotati)}</span>
                        {c.ndp.map((n) => (
                          <BadgeNdp key={n} numero={n} />
                        ))}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td
                    className={cn(
                      "px-3 py-2.5 text-right font-semibold tabular-nums",
                      c.pallet_magazzino > 0 && c.pallet_liberi <= 0 && "text-muted-foreground"
                    )}
                  >
                    {fmt(c.pallet_liberi)}
                  </td>
                  <td className="px-3 py-2.5" data-testid="oltre-cliente">
                    <span className="inline-flex flex-wrap items-center gap-1.5">
                      {c.pallet_oltre > 0 ? (
                        <span className="font-semibold tabular-nums text-red-700">
                          {fmt(c.pallet_oltre)}
                          <span className="font-normal"> · da {c.giorni_oltre} gg</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                      {c.tolleranza_accordo && (
                        <span
                          className="rounded-full border border-border px-1.5 text-[10px] text-muted-foreground"
                          title="Accordo del cliente sui giorni per ritirare (Impostazioni → Preferenze per cliente)"
                        >
                          accordo {c.tolleranza_giorni} gg
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-muted-foreground">
                    {c.righe_programmate || "—"}
                  </td>
                  <td className="px-3 py-2.5 text-[12px]">
                    {c.ultima_offerta ? (
                      <span className="inline-flex items-center gap-1.5">
                        {data(c.ultima_offerta.quando)} · {fmt(c.ultima_offerta.pallet_disponibili)}{" "}
                        pallet
                        <FonteBadge offerta={c.ultima_offerta} />
                      </span>
                    ) : (
                      <span className="text-muted-foreground">mai</span>
                    )}
                  </td>
                  <td className="px-2">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/** Un riquadro KPI: il numero è anche il filtro (come i riquadri del tabellone). */
function RiquadroKpi({
  id,
  icona: Icona,
  label,
  valore,
  attivo,
  urgente,
  onClick,
  children,
}: {
  id: string;
  icona: typeof Truck;
  label: string;
  valore: number | undefined;
  attivo: boolean;
  /** Il numero in rosso quando non è zero. */
  urgente?: boolean;
  onClick: () => void;
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={attivo}
      data-kpi={id}
      className={cn(
        "rounded-lg border p-3 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        attivo
          ? "border-brand-700 bg-brand-50 ring-1 ring-brand-700"
          : "bg-card hover:border-brand-400 hover:bg-brand-50/50"
      )}
    >
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icona className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{label}</span>
      </div>
      <div
        className={cn("mt-1 text-2xl font-bold tabular-nums", urgente && valore && "text-red-600")}
      >
        {valore == null ? "—" : fmt(valore, 1)}{" "}
        <span className="text-sm font-normal text-muted-foreground">pallet</span>
      </div>
      <div className="mt-1 space-y-1 text-[11.5px] text-muted-foreground">{children}</div>
    </button>
  );
}

/** Quanto del pronto è già su una NDP: la parte azzurra della barra. */
function BarraPrenotati({ prenotati, totale }: { prenotati: number; totale: number }) {
  const quota = totale > 0 ? Math.min(100, (prenotati / totale) * 100) : 0;
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-emerald-100" aria-hidden>
      <div className="h-full bg-sky-500" style={{ width: `${quota}%` }} />
    </div>
  );
}

function BadgeNdp({ numero, title }: { numero: string; title?: string }) {
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-full bg-sky-100 px-1.5 py-px text-[10px] font-medium text-sky-900 dark:bg-sky-950 dark:text-sky-300"
      title={
        title ?? "Nota di prelievo aperta (senza DDT): questa merce è già destinata a un carico"
      }
    >
      <Truck className="size-2.5" />
      NDP {numero}
    </span>
  );
}

/** Sotto la categoria: oltre la tolleranza (da sollecitare), o il ritiro già concordato. */
function BadgeRitiro({ r }: { r: RigaMercePronta }) {
  if (r.ritiro_concordato) {
    return (
      <div className="mt-1 text-[10.5px] text-emerald-700" data-testid="ritiro-concordato">
        ritiro concordato{r.ritiro_concordato_il ? ` il ${data(r.ritiro_concordato_il)}` : ""}
      </div>
    );
  }
  if (!oltre(r)) return null;
  const parte = (r.pallet_oltre ?? 0) < (r.pallet_liberi ?? 0);
  return (
    <div
      className="mt-1 text-[10.5px] font-semibold text-red-700"
      data-testid="oltre-riga"
      title={
        `Andava ritirata entro il ${data(r.ritiro_entro)}: ${r.tolleranza_giorni} giorni dalla ` +
        `consegna, o dall'entrata a magazzino se è venuta dopo.`
      }
    >
      {parte ? `${fmt(r.pallet_oltre)} plt ` : ""}oltre di {r.giorni_oltre} gg
    </div>
  );
}

function FonteBadge({ offerta }: { offerta: OffertaMerceBreve }) {
  return offerta.fonte === "aida" ? (
    <span
      title="Vista passare da Aida in una mail"
      className="inline-flex items-center gap-0.5 rounded-full border border-violet-200 bg-violet-50 px-1.5 text-[10px] font-semibold text-violet-700"
    >
      <Bot className="h-3 w-3" /> Aida
    </span>
  ) : (
    <span className="rounded-full border border-brand-200 bg-brand-50 px-1.5 text-[10px] font-semibold text-brand-700">
      portale
    </span>
  );
}

// ── Il dettaglio di un cliente ────────────────────────────────────────────

const BADGE: Record<CategoriaMerce, { label: string; cls: string }> = {
  magazzino: { label: "Magazzino", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  programmato: { label: "Programmato", cls: "border-blue-200 bg-blue-50 text-blue-700" },
  non_disponibile: {
    label: "Non disponibile",
    cls: "border-border bg-muted text-muted-foreground",
  },
};

/** A magazzino, ma in parte già su una NDP aperta: nell'offerta va solo il resto. */
const inParteNdp = (r: RigaMercePronta) =>
  r.categoria === "magazzino" && (r.qty_prenotata ?? 0) > 0 && (r.qty_libera ?? 0) > 0;
/** A magazzino e tutta su NDP aperte: non si offre di nuovo (non spuntata di default). */
const tuttaNdp = (r: RigaMercePronta) =>
  r.categoria === "magazzino" && (r.qty_prenotata ?? 0) > 0 && (r.qty_libera ?? 0) <= 0;
/** Il pronto che si propone di default: a magazzino e non già tutto su una NDP. */
const daOffrire = (r: RigaMercePronta) => r.categoria === "magazzino" && !tuttaNdp(r);

/** I pallet che una riga porta nell'offerta: a terra se è a magazzino (la parte
 * libera, se una NDP aperta ne ha presa un po'), previsti se no. Lo stesso conto
 * di `da_offrire` nel server, che fa il file. */
const palletDi = (r: RigaMercePronta) =>
  r.categoria !== "magazzino"
    ? (r.pallet_previsti ?? 0)
    : inParteNdp(r)
      ? (r.pallet_liberi ?? 0)
      : (r.pallet_disponibili ?? 0);
/** La quantità che una riga a magazzino porta nell'offerta (e nel carico). */
const qtyDi = (r: RigaMercePronta) => (inParteNdp(r) ? r.qty_libera : r.qty_magazzino);

function DettaglioCliente({ codice, onIndietro }: { codice: number; onIndietro: () => void }) {
  const { data: dati, isLoading } = useMerceProntaCliente(codice);
  const [filtro, setFiltro] = useState<(typeof FILTRI)[number]["id"]>("tutto");
  const [scelte, setScelte] = useState<Set<string> | null>(null);
  const [colonne, setColonne] = useState<string[] | null>(null);
  const [mail, setMail] = useState(false);
  const [carico, setCarico] = useState<InizioNuovoCarico | null>(null);
  const scarica = useScaricaOfferta(codice);
  const [errore, setErrore] = useState<string | null>(null);

  // Tutto il pronto spuntato, il resto no: Marina toglie, non aggiunge. Le righe
  // già tutte su una NDP aperta restano visibili ma non spuntate: il cliente le
  // ha già scelte, rimetterle nel file sarebbe offrirgliele due volte.
  useEffect(() => {
    if (!dati || scelte) return;
    setScelte(
      new Set(dati.commesse.flatMap((c) => c.righe.filter(daOffrire).map((r) => r.riga_id)))
    );
    setColonne(dati.colonne);
  }, [dati, scelte]);

  const tutte = useMemo(() => dati?.commesse.flatMap((c) => c.righe) ?? [], [dati]);
  const conteggi = useMemo(() => {
    const out: Record<string, number> = { tutto: tutte.length };
    for (const r of tutte) {
      out[r.categoria] = (out[r.categoria] ?? 0) + 1;
      if (oltre(r)) out.oltre = (out.oltre ?? 0) + 1;
    }
    return out;
  }, [tutte]);
  const scelteRighe = useMemo(() => tutte.filter((r) => scelte?.has(r.riga_id)), [tutte, scelte]);
  const prenotato = useMemo(() => {
    let magazzino = 0;
    let pallet = 0;
    const ndp = new Set<string>();
    for (const r of tutte) {
      if (r.categoria !== "magazzino") continue;
      magazzino += r.pallet_disponibili ?? 0;
      pallet += r.pallet_prenotati ?? 0;
      if ((r.qty_prenotata ?? 0) > 0) r.ndp.forEach((n) => ndp.add(n));
    }
    // In ordine di numero, come nell'elenco dei clienti (le NDP sono numeri; il
    // confronto «numeric» tiene in fondo le rare sigle come «557/UE»).
    return {
      magazzino,
      pallet,
      ndp: [...ndp].sort((x, y) => x.localeCompare(y, "it", { numeric: true })),
    };
  }, [tutte]);
  const fermi = useMemo(() => {
    const righe = tutte.filter(oltre);
    return {
      pallet: righe.reduce((t, r) => t + (r.pallet_oltre ?? 0), 0),
      giorni: Math.max(0, ...righe.map((r) => r.giorni_oltre ?? 0)),
    };
  }, [tutte]);
  const totali = useMemo(() => {
    let disp = 0;
    let prev = 0;
    for (const r of scelteRighe) {
      if (r.categoria === "magazzino") disp += palletDi(r);
      else prev += palletDi(r);
    }
    return {
      disp,
      prev,
      commesse: new Set(scelteRighe.map((r) => r.ordine_numero)).size,
    };
  }, [scelteRighe]);

  if (isLoading || !dati || !scelte || !colonne) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const visibile = (r: RigaMercePronta) =>
    filtro === "tutto" || (filtro === "oltre" ? oltre(r) : r.categoria === filtro);
  const cambia = (ids: string[], on: boolean) =>
    setScelte((s) => {
      const n = new Set(s);
      for (const id of ids) {
        if (on) n.add(id);
        else n.delete(id);
      }
      return n;
    });
  const corpo = { righe: scelteRighe.map((r) => r.riga_id), colonne };
  const nessuna = scelteRighe.length === 0;

  function scaricaExcel() {
    setErrore(null);
    scarica.mutate(
      { tipo: "excel", body: corpo },
      { onError: (e) => setErrore(e instanceof ApiError ? e.message : "Download non riuscito.") }
    );
  }

  function apriCarico() {
    if (!dati) return;
    const numeri = [...new Set(scelteRighe.map((r) => r.ordine_numero))];
    const righeCommesse = dati.commesse.filter((c) => numeri.includes(c.numero));
    setCarico({
      clienteNome: dati.cliente_ragione_sociale,
      clienteCodice: dati.cliente_codice,
      commesse: righeCommesse.map((c) => ({ numero: c.numero, riferimento: c.riferimento })),
      scelte: Object.fromEntries(
        righeCommesse.flatMap((c) =>
          c.righe.map((r) => {
            const scelta = scelte?.has(r.riga_id) ?? false;
            // A magazzino in parte: si carica quello che c'è (e non è già su una NDP).
            const colli = r.categoria === "magazzino" ? qtyDi(r) : r.qty_residua;
            return [r.riga_id, { escluso: !scelta, colli: colli ?? r.qty_residua }];
          })
        )
      ),
    });
  }

  return (
    <div className="space-y-4 pb-24">
      {/* Testata */}
      <div className="flex items-start gap-3">
        <button
          onClick={onIndietro}
          title="Torna ai clienti"
          className="mt-0.5 rounded-md border border-border p-1.5 hover:bg-muted"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold">{dati.cliente_ragione_sociale}</h2>
          <p className="text-[12.5px] text-muted-foreground">
            {dati.commesse.length} commesse aperte · situazione al {dataOra(dati.situazione_al)}
          </p>
          {prenotato.pallet > 0 && (
            <p
              className="mt-1 flex flex-wrap items-center gap-1.5 text-[12.5px]"
              data-testid="prenotato-cliente"
            >
              <span>
                Di {fmt(prenotato.magazzino)} pallet a magazzino,{" "}
                <b className="text-sky-900">{fmt(prenotato.pallet)} già su NDP aperte</b> e{" "}
                <b>{fmt(prenotato.magazzino - prenotato.pallet)} liberi</b>:
              </span>
              {prenotato.ndp.map((n) => (
                <BadgeNdp key={n} numero={n} />
              ))}
            </p>
          )}
          <p className="mt-1 text-[12.5px]" data-testid="tolleranza-cliente">
            <AlarmClock className="mr-1 inline h-3.5 w-3.5 text-muted-foreground" />
            Tolleranza sul ritiro: <b>{dati.tolleranza_giorni} giorni</b>{" "}
            <span className="text-muted-foreground">
              {dati.tolleranza_accordo ? "(accordo del cliente)" : "(linea guida)"}
            </span>
            {fermi.pallet > 0 ? (
              <>
                {" "}
                ·{" "}
                <b className="text-red-700">
                  {fmt(fermi.pallet)} pallet oltre, il più vecchio da {fermi.giorni} giorni
                </b>
              </>
            ) : (
              <span className="text-muted-foreground"> · niente oltre la tolleranza</span>
            )}
          </p>
        </div>
      </div>

      {/* Filtri e scelta rapida */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex overflow-hidden rounded-md border border-border" role="tablist">
          {FILTRI.map((f) => (
            <button
              key={f.id}
              role="tab"
              aria-selected={filtro === f.id}
              onClick={() => setFiltro(f.id)}
              className={cn(
                "px-3 py-1.5 text-[12px] font-semibold",
                filtro === f.id
                  ? "bg-brand-50 text-brand-700"
                  : "bg-card text-muted-foreground hover:bg-muted"
              )}
            >
              {f.label} <span className="font-normal tabular-nums">({conteggi[f.id] ?? 0})</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-[12px]">
          <button
            className="text-brand-700 hover:underline"
            title="Tutta la merce a magazzino non già su una NDP aperta"
            onClick={() => setScelte(new Set(tutte.filter(daOffrire).map((r) => r.riga_id)))}
          >
            Tutto il pronto
          </button>
          <span className="text-muted-foreground">·</span>
          <button className="text-brand-700 hover:underline" onClick={() => setScelte(new Set())}>
            Nessuna
          </button>
        </div>
        <SceltaColonne
          disponibili={dati.colonne_disponibili}
          scelte={colonne}
          onCambia={setColonne}
        />
      </div>

      {/* Le commesse */}
      <div className="space-y-3" data-testid="commesse-pronte">
        {dati.commesse.map((c) => {
          const righe = c.righe.filter(visibile);
          if (!righe.length) return null;
          const ids = righe.map((r) => r.riga_id);
          const quante = ids.filter((id) => scelte.has(id)).length;
          const palletScelti = c.righe
            .filter((r) => scelte.has(r.riga_id))
            .reduce((t, r) => t + palletDi(r), 0);
          return (
            <section
              key={c.numero}
              data-commessa={c.numero}
              className="overflow-hidden rounded-xl border border-border bg-card shadow-sm"
            >
              <header className="flex items-center gap-3 border-b border-border bg-brand-50/40 px-3 py-2 text-[12.5px]">
                <input
                  type="checkbox"
                  aria-label={`Tutta la commessa ${c.numero}`}
                  checked={quante === ids.length}
                  ref={(el) => {
                    if (el) el.indeterminate = quante > 0 && quante < ids.length;
                  }}
                  onChange={(e) => cambia(ids, e.target.checked)}
                />
                <span className="font-bold">Commessa {c.numero}</span>
                <span className="text-muted-foreground">impegno {data(c.data_impegno)}</span>
                {c.riferimento && (
                  <span className="text-muted-foreground">· rif. {c.riferimento}</span>
                )}
                <span className="text-muted-foreground">· consegna {data(c.data_consegna)}</span>
                <span className="ml-auto tabular-nums text-muted-foreground">
                  {quante ? `${fmt(palletScelti)} pallet scelti` : "nessuna riga scelta"}
                </span>
              </header>
              <table className="w-full border-collapse text-[12.5px]">
                <tbody>
                  {righe.map((r) => (
                    <tr
                      key={r.riga_id}
                      data-riga={r.riga_id}
                      className={cn(
                        "border-b border-border last:border-0",
                        !scelte.has(r.riga_id) && "text-muted-foreground"
                      )}
                    >
                      <td className="w-9 px-3 py-1.5">
                        <input
                          type="checkbox"
                          aria-label={`Riga ${r.codice_alternativo ?? r.codice}`}
                          checked={scelte.has(r.riga_id)}
                          onChange={(e) => cambia([r.riga_id], e.target.checked)}
                        />
                      </td>
                      <td className="w-40 px-2 py-1.5">
                        <div className="font-semibold">{r.codice_alternativo ?? r.codice}</div>
                        {r.codice_alternativo && (
                          <div className="text-[10.5px] text-muted-foreground">{r.codice}</div>
                        )}
                        {r.ndp.length > 0 && (
                          <div className="mt-0.5 flex flex-wrap gap-1" data-testid="ndp-riga">
                            {r.ndp.map((n) => (
                              <BadgeNdp
                                key={n}
                                numero={n}
                                title={
                                  r.categoria === "magazzino"
                                    ? `Nota di prelievo aperta (senza DDT): ${fmt(r.qty_prenotata, 0)} di ${fmt(r.qty_magazzino, 0)} a magazzino già destinati a un carico`
                                    : `Nota di prelievo aperta (senza DDT) per ${fmt(r.qty_ndp, 0)}: per noi la merce non è ancora a magazzino`
                                }
                              />
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5">{r.descrizione}</td>
                      <td
                        className="w-28 px-2 py-1.5 text-right tabular-nums"
                        title="Residuo da spedire"
                      >
                        {fmt(r.qty_residua, 0)}
                        {r.parziale && (
                          <div className="text-[10.5px] text-amber-700">
                            a magazzino {fmt(r.qty_magazzino, 0)}
                          </div>
                        )}
                      </td>
                      <td className="w-28 px-2 py-1.5 text-right font-semibold tabular-nums">
                        {tuttaNdp(r) ? (
                          <span className="text-sky-900">
                            {fmt(r.pallet_disponibili)}{" "}
                            <span className="text-[10.5px] font-normal">plt</span>
                            <div className="text-[10.5px] font-normal">tutti su NDP</div>
                          </span>
                        ) : (
                          <>
                            {fmt(palletDi(r))}{" "}
                            <span className="text-[10.5px] font-normal">plt</span>
                            {inParteNdp(r) && (
                              <div className="text-[10.5px] font-normal text-sky-900">
                                liberi · {fmt(r.pallet_prenotati)} su NDP
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td className="w-44 px-2 py-1.5">
                        <span
                          className={cn(
                            "rounded-full border px-2 py-0.5 text-[10.5px] font-semibold",
                            BADGE[r.categoria].cls
                          )}
                        >
                          {BADGE[r.categoria].label}
                          {r.categoria === "programmato" && r.disponibile_dal
                            ? ` · dal ${data(r.disponibile_dal)}`
                            : ""}
                        </span>
                        <BadgeRitiro r={r} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          );
        })}
      </div>

      <StoricoOfferte offerte={dati.offerte} />

      {/* La barra in fondo: cosa si sta offrendo, e cosa farne */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card/95 px-6 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.04)] backdrop-blur lg:pr-[72px]">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-3">
          <div className="text-[13px]" data-testid="riepilogo-scelta">
            <b className="tabular-nums">{scelteRighe.length}</b> righe ·{" "}
            <b className="tabular-nums">{totali.commesse}</b> commesse ·{" "}
            <b className="tabular-nums">{fmt(totali.disp)} pallet</b> a magazzino
            {totali.prev > 0 && (
              <span className="text-blue-700"> + {fmt(totali.prev)} in arrivo</span>
            )}
          </div>
          {errore && <span className="text-[12px] text-red-700">{errore}</span>}
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={nessuna} onClick={apriCarico}>
              <Truck className="mr-1.5 h-4 w-4" /> Crea carico
            </Button>
            <Button variant="outline" size="sm" disabled={nessuna} onClick={() => setMail(true)}>
              <Mail className="mr-1.5 h-4 w-4" /> Prepara mail
            </Button>
            <Button size="sm" disabled={nessuna || scarica.isPending} onClick={scaricaExcel}>
              {scarica.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="mr-1.5 h-4 w-4" />
              )}
              Scarica Excel
            </Button>
          </div>
        </div>
      </div>

      {mail && (
        <MailDialog
          dati={dati}
          righe={scelteRighe.map((r) => r.riga_id)}
          colonne={colonne}
          onClose={() => setMail(false)}
        />
      )}
      {carico && <NuovoCaricoDialog iniziale={carico} onClose={() => setCarico(null)} />}
    </div>
  );
}

function SceltaColonne({
  disponibili,
  scelte,
  onCambia,
}: {
  disponibili: { chiave: string; intestazione: string }[];
  scelte: string[];
  onCambia: (c: string[]) => void;
}) {
  const [aperto, setAperto] = useState(false);
  return (
    <div className="relative ml-auto">
      <Button variant="outline" size="sm" onClick={() => setAperto((a) => !a)}>
        <Columns3 className="mr-1.5 h-4 w-4" /> Colonne del file ({scelte.length})
      </Button>
      {aperto && (
        <div className="absolute right-0 z-30 mt-1 w-60 rounded-lg border border-border bg-card p-2 shadow-lg">
          <div className="mb-1 flex items-center justify-between px-1 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
            Colonne dell'Excel
            <button onClick={() => setAperto(false)} aria-label="Chiudi">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {disponibili.map((c) => (
            <label
              key={c.chiave}
              className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[12.5px] hover:bg-muted"
            >
              <input
                type="checkbox"
                checked={scelte.includes(c.chiave)}
                onChange={(e) =>
                  onCambia(
                    e.target.checked
                      ? disponibili
                          .map((d) => d.chiave)
                          .filter((k) => k === c.chiave || scelte.includes(k))
                      : scelte.filter((k) => k !== c.chiave)
                  )
                }
              />
              {c.intestazione}
            </label>
          ))}
          <p className="mt-1 px-1.5 text-[10.5px] text-muted-foreground">
            Restano ricordate per questo cliente. «disponibile dal» si aggiunge da sola se offri
            righe non ancora pronte.
          </p>
        </div>
      )}
    </div>
  );
}

function StoricoOfferte({ offerte }: { offerte: OffertaMerceBreve[] }) {
  return (
    <section
      className="rounded-xl border border-border bg-card p-4 shadow-sm"
      data-testid="storico-offerte"
    >
      <h3 className="mb-2 flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide text-muted-foreground">
        <PackageCheck className="h-4 w-4" /> Offerte a questo cliente
      </h3>
      {offerte.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground">Nessuna offerta registrata finora.</p>
      ) : (
        <ul className="divide-y divide-border text-[12.5px]">
          {offerte.map((o) => (
            <li key={o.id} className="flex flex-wrap items-center gap-2 py-1.5">
              <span className="w-32 tabular-nums">{dataOra(o.quando)}</span>
              <FonteBadge offerta={o} />
              <span>
                {o.n_righe} righe · commesse {o.commesse.join(", ")} ·{" "}
                <b>{fmt(o.pallet_disponibili)} pallet</b>
                {o.pallet_programmati > 0 && ` + ${fmt(o.pallet_programmati)} in arrivo`}
              </span>
              <span className="ml-auto text-[11.5px] text-muted-foreground">
                {o.inviata_il ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <Check className="h-3.5 w-3.5" /> inviata
                    {o.destinatari.length ? ` a ${o.destinatari.join(", ")}` : ""}
                  </span>
                ) : (
                  `preparata (${o.canali.join(" + ")}), non ancora vista partire`
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── La bozza di mail ──────────────────────────────────────────────────────

const elenco = (testo: string) =>
  testo
    .split(/[;,\s]+/)
    .map((x) => x.trim())
    .filter((x) => x.includes("@"));

function MailDialog({
  dati,
  righe,
  colonne,
  onClose,
}: {
  dati: MerceProntaCliente;
  righe: string[];
  colonne: string[];
  onClose: () => void;
}) {
  const proposta = useMailOffertaProposta(dati.cliente_codice);
  const scarica = useScaricaOfferta(dati.cliente_codice);
  const [lingua, setLingua] = useState("it");
  const [a, setA] = useState("");
  const [cc, setCc] = useState("");
  const [oggetto, setOggetto] = useState("");
  const [corpo, setCorpo] = useState("");
  const [fatto, setFatto] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const { mutate } = proposta;

  useEffect(() => {
    mutate(
      { righe, lingua },
      {
        onSuccess: (p) => {
          setA(p.destinatari.join(", "));
          setCc(p.copia.join(", "));
          setOggetto(p.oggetto);
          setCorpo(p.corpo);
        },
      }
    );
  }, [lingua]); // eslint-disable-line react-hooks/exhaustive-deps

  function genera() {
    setErrore(null);
    scarica.mutate(
      {
        tipo: "mail",
        body: { righe, colonne, destinatari: elenco(a), copia: elenco(cc), oggetto, corpo },
      },
      {
        onSuccess: () => setFatto(true),
        onError: (e) => setErrore(e instanceof ApiError ? e.message : "Bozza non generata."),
      }
    );
  }

  const campo =
    "mt-1 w-full rounded-md border border-border bg-card px-3 py-2 text-sm outline-none focus:border-brand-500";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-brand-700" /> Mail a {dati.cliente_ragione_sociale}
          </DialogTitle>
        </DialogHeader>
        {fatto ? (
          <div className="space-y-3 text-[13px]">
            <p className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-800">
              <Check className="mt-0.5 h-4 w-4 flex-none" />
              <span>
                Bozza scaricata, con l'Excel allegato. Aprila: Outlook la mostra come una mail nuova
                — controlla e premi <b>Invia</b>. Aida è in copia: quando parte, l'offerta risulta
                inviata.
              </span>
            </p>
            <div className="flex justify-end">
              <Button onClick={onClose}>Chiudi</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-[12.5px]">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Lingua del testo</span>
              {["it", "en"].map((l) => (
                <button
                  key={l}
                  onClick={() => setLingua(l)}
                  className={cn(
                    "rounded-md border px-2 py-0.5 text-[11.5px] font-semibold uppercase",
                    lingua === l ? "border-brand-500 bg-brand-50 text-brand-700" : "border-border"
                  )}
                >
                  {l}
                </button>
              ))}
              {proposta.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            </div>
            <label className="block">
              <span className="font-medium text-muted-foreground">A</span>
              <input className={campo} value={a} onChange={(e) => setA(e.target.value)} />
            </label>
            <label className="block">
              <span className="font-medium text-muted-foreground">Cc</span>
              <input className={campo} value={cc} onChange={(e) => setCc(e.target.value)} />
            </label>
            <label className="block">
              <span className="font-medium text-muted-foreground">Oggetto</span>
              <input
                className={campo}
                value={oggetto}
                onChange={(e) => setOggetto(e.target.value)}
              />
            </label>
            <label className="block">
              <span className="font-medium text-muted-foreground">Testo</span>
              <textarea
                className={cn(campo, "h-44 resize-none font-[inherit]")}
                value={corpo}
                onChange={(e) => setCorpo(e.target.value)}
              />
            </label>
            <p className="text-[11.5px] text-muted-foreground">
              Si scarica un file .eml con l'Excel allegato: aperto, Outlook lo mostra come una mail
              nuova da inviare dalla tua casella. Puoi ancora cambiare tutto lì.
            </p>
            {errore && <p className="text-red-700">{errore}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Annulla
              </Button>
              <Button
                onClick={genera}
                disabled={scarica.isPending || proposta.isPending || elenco(a).length === 0}
              >
                {scarica.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Scarica la bozza per Outlook
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
